import {
  fetchOncologySubmissionData,
  OncologySubmissionApiResponse,
} from "../../../api/bigQuery/oncologySubmissionOrders";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { logError } from "../../../utils/customLogger";
import { OncologySubmissionFilterPayload } from "../hooks/useFilterPayload";
import { OncologySubmissionTableState } from "../hooks/useOncologySubmissionTableData";

/**
 * makePostCallForOncologySubmission - API Call for Filter/Search Changes
 *
 * Purpose: Fetches data when filters or search term changes.
 * Resets tableData to new results and increments pagination reset count.
 *
 * @param payload - Filter payload containing search params
 * @param setOncologySubmissionState - State setter for API response
 * @param setTableData - State setter for table data
 * @param setPaginationResetCount - Function to reset pagination
 */
export const makePostCallForOncologySubmission = async (
  payload: OncologySubmissionFilterPayload,
  setOncologySubmissionState: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
): Promise<void> => {
  try {
    setOncologySubmissionState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    const response: OncologySubmissionApiResponse =
      await fetchOncologySubmissionData(payload);

    if (response.success) {
      setOncologySubmissionState({
        data: response,
        loading: false,
        error: null,
        showInlineLoader: false,
      });
      setTableData(response.rows);

      // Reset pagination to page 1 when filters/search change
      setPaginationResetCount((prev) => prev + 1);
    } else {
      setOncologySubmissionState((prev) => ({
        ...prev,
        loading: false,
        error: response.message || "Failed to fetch data",
      }));
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    setOncologySubmissionState((prev) => ({
      ...prev,
      loading: false,
      error: errorMessage,
    }));
    logError(error as Error, "Error in makePostCallForOncologySubmission");
  }
};
